import React from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faPhone, faLocationDot, faHeadset, faFileContract, faWallet } from '@fortawesome/free-solid-svg-icons';

const Contact = () => {
  return (
    <div className="min-h-screen bg-white text-gray-800">
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0">
          {/* Background pattern */}
          <div 
            className="absolute inset-0 opacity-[0.15]"
            style={{
              backgroundImage: `
                linear-gradient(to right, rgba(59, 130, 246, 0.1) 1px, transparent 1px),
                linear-gradient(to bottom, rgba(59, 130, 246, 0.1) 1px, transparent 1px)
              `,
              backgroundSize: '50px 50px'
            }}
          />
          
          {/* Animated Glow Effects */}
          <div 
            className="absolute -top-40 -left-40 w-80 h-80 bg-blue-200 rounded-full filter blur-[100px] opacity-20 animate-pulse"
            style={{ animationDuration: '4s' }}
          />
          <div 
            className="absolute -bottom-40 -right-40 w-80 h-80 bg-green-200 rounded-full filter blur-[100px] opacity-20 animate-pulse"
            style={{ animationDuration: '6s' }}
          />
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-gradient gradient-text">
              Get in Touch
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 mb-8 leading-relaxed">
              Have questions about integrating UniPay into your business? We're here to help!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 relative bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-2xl font-bold mb-8 text-gradient gradient-text">
                Contact Information
              </h2>
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="bg-primary/10 w-12 h-12 rounded-lg flex items-center justify-center">
                    <FontAwesomeIcon icon={faEnvelope} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-gray-800 font-semibold">Email</h3>
                    <p className="text-gray-600">contact@unipay.io</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="bg-primary/10 w-12 h-12 rounded-lg flex items-center justify-center">
                    <FontAwesomeIcon icon={faPhone} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-gray-800 font-semibold">Phone</h3>
                    <p className="text-gray-600">+1 (555) 123-4567</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="bg-primary/10 w-12 h-12 rounded-lg flex items-center justify-center">
                    <FontAwesomeIcon icon={faLocationDot} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-gray-800 font-semibold">Location</h3>
                    <p className="text-gray-600">San Francisco, CA</p>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-bold mt-12 mb-8 text-gradient gradient-text">
                Our Departments
              </h2>
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="bg-primary/10 w-12 h-12 rounded-lg flex items-center justify-center">
                    <FontAwesomeIcon icon={faHeadset} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-gray-800 font-semibold">Technical Support</h3>
                    <p className="text-gray-600">support@unipay.io</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="bg-primary/10 w-12 h-12 rounded-lg flex items-center justify-center">
                    <FontAwesomeIcon icon={faFileContract} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-gray-800 font-semibold">Business Inquiries</h3>
                    <p className="text-gray-600">sales@unipay.io</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="bg-primary/10 w-12 h-12 rounded-lg flex items-center justify-center">
                    <FontAwesomeIcon icon={faWallet} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-gray-800 font-semibold">Partnerships</h3>
                    <p className="text-gray-600">partners@unipay.io</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-xl">
              <form className="space-y-6">
                <div>
                  <label className="block text-gray-700 mb-2" htmlFor="name">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-gray-800 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors duration-300"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-2" htmlFor="email">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-gray-800 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors duration-300"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-2" htmlFor="subject">
                    Subject
                  </label>
                  <select
                    id="subject"
                    className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-gray-800 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors duration-300"
                  >
                    <option value="">Select a subject</option>
                    <option value="integration">Integration Support</option>
                    <option value="pricing">Pricing Inquiry</option>
                    <option value="partnership">Partnership Opportunity</option>
                    <option value="technical">Technical Question</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700 mb-2" htmlFor="message">
                    Message
                  </label>
                  <textarea
                    id="message"
                    rows="5"
                    className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-gray-800 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors duration-300"
                    placeholder="Your message..."
                  ></textarea>
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  className="w-full px-8 py-4 bg-gradient-to-r from-primary to-secondary text-white rounded-lg font-semibold shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300"
                  type="submit"
                >
                  Send Message
                </motion.button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-12 text-center text-gradient gradient-text">
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              {[
                {
                  question: "How quickly can I integrate UniPay into my application?",
                  answer: "You can integrate UniPay in just a few hours using our SDKs and comprehensive documentation. Our onboarding team is available to help with any questions during the integration process."
                },
                {
                  question: "Which cryptocurrencies does UniPay support?",
                  answer: "UniPay supports all major cryptocurrencies including Bitcoin, Ethereum, Solana, and many others across multiple blockchains. We continually add support for new cryptocurrencies based on market demand."
                },
                {
                  question: "How does the fiat settlement process work?",
                  answer: "When a customer pays in cryptocurrency, UniPay automatically converts it to your preferred fiat currency at the current market rate and deposits it into your bank account on your chosen schedule (daily, weekly, or monthly)."
                },
                {
                  question: "What are the transaction fees?",
                  answer: "Our transaction fees start at 1% and decrease based on volume. There are no hidden fees, setup costs, or monthly minimums. You only pay for successful transactions."
                },
                {
                  question: "Is UniPay compliant with financial regulations?",
                  answer: "Yes, UniPay is fully compliant with relevant financial regulations including KYC/AML requirements. We handle all the compliance complexity so you can focus on your business."
                },
                {
                  question: "Do you offer enterprise support?",
                  answer: "Yes, we offer dedicated enterprise support with 24/7 availability, custom integration assistance, priority response times, and a dedicated account manager."
                }
              ].map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-gray-50 p-6 rounded-xl border border-gray-200 shadow-md hover:shadow-lg transition-all duration-300"
                >
                  <h3 className="text-xl font-semibold mb-3 text-gray-800">{faq.question}</h3>
                  <p className="text-gray-600">{faq.answer}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary/10 to-secondary/10 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        <div className="container mx-auto px-6 relative">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl p-10 border border-gray-200 shadow-xl">
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-6 text-gradient gradient-text">
                Ready to modernize your payment infrastructure?
              </h2>
              <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                Schedule a personalized demo with our team to see how UniPay can transform your business.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  className="btn btn-primary"
                >
                  Schedule a Demo
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  className="btn btn-secondary"
                >
                  View Documentation
                </motion.button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
